import React, { useEffect, useRef, useState } from 'react';
import { Task, TaskResponse, TaskState } from 'gql/graphql';
import { CardLoanTasksProps } from 'components/Atoms/Cards/CardLoanTasks/CardLoanTasks';
import TaskProgress from 'components/Atoms/Progress/TaskProgress/TaskProgress';
import MinimumLiquidity from './MinimumLiquidity';
import { ModuleKeys } from 'services/task/modules-keys.constanst';
import { formatterCurrency } from '@common/utils/formatters/formatter';
import { useUpdateTaskState } from 'services/task/mutations';

interface Props {
  tasks?: Task[] | any[],
}

const useCardMinimumLiquidity = ({ tasks }: Props) => {
  const [dataTask, setDataTask] = useState<Task>();
  const [stepNow, setStepNow] = useState<TaskResponse>();
  const [accountsErrors, setAccountsErrors] = useState<TaskResponse[]>();
  const { execute: updateTaskState } = useUpdateTaskState();
  const updatedToDraftRef = useRef<string | null>(null);

  useEffect(() => {
    const data: Task = tasks?.find((item) => item.key === ModuleKeys.MINIMUM_LIQUIDITY);
    setDataTask(data);
    
    const _accountsErrors = data?.responses.filter(item => item.state === TaskState.RequestError);
    setAccountsErrors(_accountsErrors);

    if(data?.responses.length > 0) 
      setStepNow(data?.responses.reduce((max, obj) => obj.step > max.step ? obj : max, data?.responses[0]));

  }, [tasks]);

  useEffect(() => {
    if (!dataTask || dataTask.state !== TaskState.ComingSoon) return;
    if (updatedToDraftRef.current === dataTask._id) return;
    if (dataTask.metadata?.jungoLimitBalance == null || dataTask.metadata?.jungoLimitBalance === '') return;
    updatedToDraftRef.current = dataTask._id;
    updateTaskState({
      updateTaskStateInput: {
        _id: dataTask._id,
        key: ModuleKeys.MINIMUM_LIQUIDITY,
        state: TaskState.Draft
      }
    });
  }, [dataTask?._id, dataTask?.state, dataTask?.metadata?.jungoLimitBalance]);
  
  const isComingSoon = dataTask?.state === TaskState.ComingSoon;

  const card: CardLoanTasksProps =
  {
    key: ModuleKeys.MINIMUM_LIQUIDITY,
    title: <h4 className={`h4 ${isComingSoon && 'disabled'}`}>Proof of Liquidity</h4>,
    steps: 2,
    stepCurrent: stepNow?.step || 0,
    disabled: isComingSoon,
    stateTooltipHtml: `<span>We're determining your minimum liquidity requirements</b><span> to unlock this</span>`,
    type: 'Major task',
    text2: dataTask?.state === TaskState.Approved ?
      <div className="message-state-success">
        <span>Your docs have been approved!</span>
      </div>
      :
      dataTask?.state === TaskState.RequestError ?
        <div className="message-state-error">
          <span>
            We were unable to approve {accountsErrors?.length} of your
            accounts
          </span>
        </div>
        :
        <p className="body-card" style={{ marginTop: 5 }}>
          {dataTask?.state === TaskState.Review ?
            `Your docs are in review and you’ve shown the minimum account balance. No more action needed at this time`
            :
            dataTask?.metadata.totalBalance.review > 0 ?
              `We’re reviewing the docs you submitted, but we need proof of a higher account balance`
              :
              null
          }
        </p>,
    progress: (
      <TaskProgress
        title={<h4 className={`body-card body-card-w5 ${isComingSoon && 'disabled'}`}>Initial liquidity</h4>}
        price={dataTask?.metadata.jungoLimitBalance ? <h4 className="body-card body-card-w5">{formatterCurrency(dataTask?.metadata.jungoLimitBalance)}</h4> : null}
        colorBackground="#DFE4EB)"
        balances={dataTask?.metadata?.totalBalance}
        totalBalance={dataTask?.metadata.jungoLimitBalance}
        className={isComingSoon? `disabled`: ''}
      />
    ),
    state: dataTask?.state,
    actions: dataTask ? [<MinimumLiquidity key={1} task={dataTask} maxBalance={dataTask?.metadata.jungoLimitBalance}/>] : []
  };

  if(!dataTask) 
    return { card: null };
  
  return {
    card,
    dataTask
  };
};

export default useCardMinimumLiquidity;
